import { Controller } from "@hotwired/stimulus"

// Uploads the music video artwork on its own, out of band from the form.
//
// Checks what it can in the browser first - type, size, aspect ratio, minimum
// resolution - so an obviously wrong file never leaves the machine. The
// uploader re-checks everything server-side.
export default class extends Controller {
  static targets = ["input", "preview", "placeholder", "progress", "progressBar", "error"]
  static values = { uploadUrl: String }

  static ALLOWED_TYPES = ["image/jpeg", "image/png"]
  static MAX_SIZE = 50 * 1024 * 1024
  static MIN_WIDTH = 1920
  static MIN_HEIGHT = 1080
  static RATIOS = [16 / 9, 4 / 3]
  static RATIO_TOLERANCE = 0.02

  browse() {
    this.inputTarget.click()
  }

  async fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    this.hideError()

    if (!this.constructor.ALLOWED_TYPES.includes(file.type)) {
      return this.failWith(this.t("music_videos.edit.validation.thumbnail_type"))
    }

    if (file.size > this.constructor.MAX_SIZE) {
      return this.failWith(this.t("music_videos.edit.validation.thumbnail_size"))
    }

    const dimensions = await this.readDimensions(file)
    if (dimensions) {
      const { width, height } = dimensions

      if (width < this.constructor.MIN_WIDTH || height < this.constructor.MIN_HEIGHT) {
        return this.failWith(this.t("music_videos.edit.validation.thumbnail_min_size", { width, height }))
      }

      const ratio = width / height
      const allowed = this.constructor.RATIOS.some(r => Math.abs(ratio - r) <= this.constructor.RATIO_TOLERANCE)
      if (!allowed) {
        return this.failWith(this.t("music_videos.edit.validation.thumbnail_aspect", { width, height }))
      }
    }

    this.upload(file)
  }

  readDimensions(file) {
    return new Promise(resolve => {
      const url = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        URL.revokeObjectURL(url)
        resolve({ width: image.width, height: image.height })
      }
      image.onerror = () => {
        URL.revokeObjectURL(url)
        resolve(null)
      }
      image.src = url
    })
  }

  upload(file) {
    const formData = new FormData()
    formData.append("thumbnail", file)

    const xhr = new XMLHttpRequest()
    xhr.open("POST", this.uploadUrlValue, true)
    xhr.setRequestHeader("X-CSRF-Token", document.querySelector('meta[name="csrf-token"]').content)
    xhr.setRequestHeader("Accept", "application/json")

    this.showProgress(0)

    xhr.upload.addEventListener("progress", event => {
      if (event.lengthComputable) {
        this.showProgress(Math.round((event.loaded / event.total) * 100))
      }
    })

    xhr.onload = () => {
      this.hideProgress()
      this.inputTarget.value = ""

      let payload = {}
      try {
        payload = JSON.parse(xhr.responseText)
      } catch (e) {
        return this.failWith(this.t("music_videos.edit.validation.thumbnail_failed"))
      }

      if (xhr.status >= 200 && xhr.status < 300 && payload.success) {
        this.showPreview(payload.thumbnail_url)
        // Lets the full-size preview pick up the new image without a reload.
        this.dispatch("uploaded", { detail: { fullUrl: payload.full_url, downloadUrl: payload.download_url } })
      } else {
        this.failWith((payload.errors || []).join(", ") || this.t("music_videos.edit.validation.thumbnail_failed"))
      }
    }

    xhr.onerror = () => {
      this.hideProgress()
      this.failWith(this.t("music_videos.edit.validation.thumbnail_failed"))
    }

    xhr.send(formData)
  }

  showPreview(url) {
    if (!url || !this.hasPreviewTarget) return

    this.previewTarget.src = url
    this.previewTarget.classList.remove("hidden")
    // The form controller gates Save and Continue on this.
    this.element.dataset.hasThumbnail = "true"

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }
  }

  showProgress(percent) {
    if (!this.hasProgressTarget) return

    this.progressTarget.classList.remove("hidden")
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
  }

  hideProgress() {
    if (this.hasProgressTarget) this.progressTarget.classList.add("hidden")
  }

  failWith(message) {
    this.inputTarget.value = ""
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  t(key, options = {}) {
    if (window.I18n && typeof window.I18n.t === "function") {
      return window.I18n.t(key, options)
    }
    return key
  }
}
